export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  permissions: string[];
}

export interface Product {
  id: string;
  item_id: string;
  category: string;
  item_name: string;
  manufacturer: string;
  device: string;
  sku: string;
  cost_price_int: number;
  cost_price_ext: number;
  retail_price_int: number;
  retail_price_ext: number;
  promotional_price: number;
  tax_class: string;
  repair_time: number;
}

export interface PriceHistory {
  id: string;
  product_id: string;
  purchase_price_int: number;
  purchase_price_ext: number;
  internal_price: number;
  external_price: number;
  repair_time: number;
  updated_by: string;
  created_at: string;
  action: string;
}

export interface CalculationParams {
  internal_minute_cost: number;
  external_minute_cost: number;
}

export const PERMISSIONS = {
  PRICE_MANAGEMENT: 'Gestion de Prix',
  PRICE_LIST: 'Liste des Prix',
  SHOP_PRICE_LIST: 'Liste des Prix Boutique',
  PARTS_MANAGEMENT: 'Gestion Des pièces sur commande',
  PARTS_LIST: 'Liste des pièces sur commande',
  NEW_ORDER: 'Nouvelle Commande',
  NEW_DISPUTE: 'Nouveau Litige',
  INSURANCE_DISPUTE: 'Gestion Litige Assurance',
  DISPUTE_MANAGEMENT: 'Gestion Litige',
  PAYMENT_TRACKING: 'Suivi Paiement',
  DASHBOARD_FULL: 'Tableau de Bord Complet',
  DASHBOARD_PARTIAL: 'Tableau de Bord Partiel',
  DASHBOARD_NONE: 'Sans Tableau de Bord'
} as const;

export interface PriceFilters {
  category?: string;
  manufacturer?: string;
  device?: string;
  search?: string;
  page: number;
  pageSize: number;
}

export interface PartsOrder {
  id: string;
  ticket_number: string;
  laboratory: 'Laboratoire Central' | 'Laboratoire La Marsa';
  client_name: string;
  client_phone: string;
  manufacturer: string;
  custom_manufacturer?: string;
  parts_description: string;
  estimated_price: number;
  advance_payment: number;
  payment_method: 'Espèce' | 'Virement' | 'TPE' | 'Chèque';
  product_link?: string;
  order_date: string;
  purchase_date?: string;
  expected_date?: string;
  supplier?: string;
  supplier_address?: string;
  purchase_price?: number;
  status: 'En Attente' | 'Commandé' | 'Commande Finalisé';
  created_at: string;
  updated_at: string;
  parts_complaints?: PartsComplaint[];
}

export interface PartsOrderHistory {
  id: string;
  order_id: string;
  action: string;
  details: any;
  created_by: string;
  created_at: string;
}

export interface PartsComplaint {
  id: string;
  order_id: string;
  description: string;
  status: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface Manufacturer {
  id: string;
  name: string;
  created_at: string;
}

export interface PartsOrderFilters {
  status?: string;
  search?: string;
  page: number;
  pageSize: number;
}

export interface DisputeDeviceType {
  id: string;
  name: string;
  category: string;
  created_at: string;
}

// Interfaces for dispute management
export interface Dispute {
  id: string;
  case_number: string;
  created_at: string;
  client_name: string;
  manufacturer: string;
  custom_manufacturer?: string;
  device_type: string;
  problem: string;
  zone: string;
  purchase_date: string;
  store: string;
  client_address: string;
  phone1: string;
  phone2?: string;
  technician?: string;
  technician_phone?: string;
  expected_date?: string;
  technician_quote?: TechnicianQuote[];
  technician_quote_total?: number;
  insurance_quote_file?: string;
  insurance_quote_amount?: number;
  purchase_order_file?: string;
  invoice_file?: string;
  insurance_status: DisputeStatusInsurance;
  management_status: DisputeStatusManagement;
  updated_at: string;
  dispute_complaints?: { id: string }[];
}

export interface TechnicianQuote {
  description: string;
  price: number;
}

export type DisputeStatusInsurance =
  | 'Envoyé'
  | 'Pris en Charge'
  | 'Devis Technicien'
  | 'Devis Reçu'
  | 'BC envoyé'
  | 'Facturation';

export type DisputeStatusManagement =
  | 'Envoyé'
  | 'Pris en Charge'
  | 'Devis Technicien'
  | 'Devis Envoyé'
  | 'Attente BC'
  | 'BC Reçu'
  | 'Facturation';

export type PaymentStatus = 'En cours' | 'Acompte' | 'Dépôt' | 'Payé';

export interface DisputeHistory {
  id: string;
  dispute_id: string;
  action: string;
  details: any;
  created_by: string;
  created_at: string;
}

export interface DisputeComplaint {
  id: string;
  dispute_id: string;
  description: string;
  created_by: string;
  created_at: string;
}

export interface DisputePayment {
  id: string;
  dispute_id: string;
  payment_type: 'technician' | 'insurance';
  payment_date?: string;
  amount?: number;
  payment_method?: 'Espèce' | 'Virement' | 'Chèque';
  invoice_deposit_date?: string;
  payment_receipt_date?: string;
  transfer_number?: string;
  status: PaymentStatus;
  created_at: string;
  updated_at: string;
}

export interface DisputeFilters {
  status?: DisputeStatusInsurance | DisputeStatusManagement;
  search?: string;
  page: number;
  pageSize: number;
}

// Chart data types
export interface ChartData {
  disputeStatusChart: {
    labels: string[];
    values: number[];
  };
  revenueChart: {
    labels: string[];
    disputes: number[];
    parts: number[];
  };
}

export interface DashboardStats extends ChartData {
  todayOrders: number;
  activeDisputes: number;
  disputesRevenue: number;
  partsRevenue: number;
  totalProducts: number;
  pendingParts: number;
}